import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { getBlogPost } from "@/lib/blog.functions";
import {
  categoryLabel,
  formatDateEs,
  isoDate,
  normalizeFaq,
  type BlogPost,
  type BlogPostCard,
} from "@/lib/blog";
import { absoluteUrl } from "@/lib/site";
import { blogPostSchemas } from "@/lib/blog-schema";
import { relatedPagesFor } from "@/lib/related-pages";
import { waLink } from "@/lib/whatsapp";
import { PostBody } from "@/components/blog/PostBody";
import { PostFaqSection } from "@/components/blog/PostFaqSection";
import { ArticleAuthorCard } from "@/components/blog/ArticleAuthorCard";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { PanelSection } from "@/components/site/PanelSection";
import { PageCta } from "@/components/site/PageCta";


export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const result = await getBlogPost({ data: { slug: params.slug } });
    if (result.kind === "redirect") {
      throw redirect({
        to: "/blog/$slug",
        params: { slug: result.to },
        statusCode: 301,
      });
    }
    if (result.kind === "missing") throw notFound();
    return { post: result.post, related: result.related };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Artículo no disponible | Oasis Cuernavaca" }, { name: "robots", content: "noindex" }],
      };
    }
    const { post } = loaderData;
    const { url, description, article, breadcrumb, faqPage } = blogPostSchemas(post);
    const published = isoDate(post.published_at);
    const modified = isoDate(post.updated_at ?? post.published_at);
    return {
      meta: [
        { title: `${post.title} | Oasis Cuernavaca` },
        { name: "description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:site_name", content: "Residencia Oasis Cuernavaca" },
        { property: "og:locale", content: "es_MX" },
        { property: "og:title", content: post.title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        ...(published ? [{ property: "article:published_time", content: published }] : []),
        ...(modified ? [{ property: "article:modified_time", content: modified }] : []),
        ...(post.category ? [{ property: "article:section", content: post.category }] : []),
        { name: "author", content: post.author || "Residencia Oasis Cuernavaca" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post.title },
        { name: "twitter:description", content: description },
        ...(post.cover_image
          ? [
              { property: "og:image", content: post.cover_image },
              { property: "og:image:alt", content: post.title },
              { name: "twitter:image", content: post.cover_image },
            ]
          : []),
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(article) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
        ...(faqPage
          ? [{ type: "application/ld+json", children: JSON.stringify(faqPage) }]
          : []),
      ],
      links: [
        { rel: "canonical", href: url },
        // LCP del artículo: la portada se descubre en el <head>, no al parsear el body.
        ...(post.cover_image
          ? [
              {
                rel: "preload",
                as: "image",
                href: post.cover_image,
                fetchPriority: "high" as const,
              },
            ]
          : []),
      ],
    };
  },
  component: BlogPostPage,
  notFoundComponent: PostNotFound,
  errorComponent: PostLoadError,
});

function PostLoadError({ reset }: { reset: () => void }) {
  return (
    <section className="container-prose section-y">
      <h1 className="h-display">No pudimos cargar el artículo</h1>
      <p className="answer-first">
        Fue un fallo momentáneo al obtener el contenido. Vuelva a intentarlo: suele resolverse al
        instante.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <button type="button" onClick={reset} className="btn-base btn-primary-cta">
          Volver a intentar
        </button>
        <Link to="/blog" search={{ page: 1 }} className="btn-base btn-outline-cta">
          Ver el blog
        </Link>
      </div>
    </section>
  );
}

function PostNotFound() {
  return (
    <section className="container-prose section-y">
      <h1 className="h-display">No encontramos este artículo</h1>
      <p className="answer-first">
        Es posible que la dirección haya cambiado. Vea todos los artículos del blog o escríbanos y
        le respondemos el mismo día.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/blog" search={{ page: 1 }} className="btn-base btn-primary-cta">
          Ver el blog
        </Link>
        <Link to="/contacto" className="btn-base btn-outline-cta">
          Contacto
        </Link>
      </div>
    </section>
  );
}

function BlogPostPage() {
  const { post, related } = Route.useLoaderData() as { post: BlogPost; related: BlogPostCard[] };
  const url = absoluteUrl(`/blog/${post.slug}`);
  const faq = normalizeFaq(post.faq);
  const relatedPages = relatedPagesFor({
    title: post.title,
    category: post.category,
    content: post.content,
  });

  return (
    <>
      <section className="section-air" aria-label={post.title}>
        <div className="container-site">
          <div className="panel-plain container-prose px-0">
            <Breadcrumbs current={post.title} />

            <p className="eyebrow mt-4">{categoryLabel(post.category)}</p>
            <h1 className="h-display mt-2">{post.title}</h1>
            <p className="mt-4 text-muted-foreground">
              <time dateTime={isoDate(post.published_at)}>{formatDateEs(post.published_at)}</time>
              {post.reading_time > 0 && <> · {post.reading_time} min de lectura</>} · {post.author}
            </p>

            {post.cover_image && (
              <img
                src={post.cover_image}
                alt={post.title}
                width={1200}
                height={675}
                loading="eager"
                fetchPriority="high"
                decoding="sync"
                className="mt-8 aspect-[16/9] w-full rounded-2xl object-cover"
              />
            )}

            <PostBody content={post.content} />
          </div>
        </div>
      </section>

      <PostFaqSection faq={faq} cta={relatedPages[0]} />

      <PanelSection tone="soft" eyebrow="Relacionado" title="Sobre este tema en Residencia Oasis">
        <ul className="grid gap-2">
          {relatedPages.map((p) => (
            <li key={p.to}>
              <Link to={p.to} className="link-arrow">
                {p.anchor}
              </Link>
            </li>
          ))}
        </ul>
      </PanelSection>


      <PanelSection tone="plain" label="Sobre el autor">
        <ArticleAuthorCard
          author={post.author}
          publishedAt={post.published_at}
          updatedAt={post.updated_at ?? null}
        />
      </PanelSection>

      {related.length > 0 && (
        <PanelSection tone="light" eyebrow="Más artículos" title="Siga leyendo">
          <ul className="grid gap-4">
            {related.map((r) => (
              <li key={r.slug}>
                <Link
                  to="/blog/$slug"
                  params={{ slug: r.slug }}
                  className="link-arrow"
                >
                  {r.title}
                </Link>
              </li>
            ))}
          </ul>
        </PanelSection>
      )}

      <PageCta
        waHref={waLink(`blog/${post.slug}`)}
        eyebrow="¿Está evaluando una residencia?"
        title="¿Cómo empezamos?"
        answer="Residencia Oasis Cuernavaca: cuidado y acompañamiento 24/7, habitación individual y visitas sin horarios restrictivos. Desde $950 MXN al día."
      />
    </>
  );
}
